import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api } from "../lib/api";

const AppCtx = createContext(null);
const USER_KEY = "harrods_pm_user";

export function AppProvider({ children }) {
  const [user, setUser] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem(USER_KEY)) || null;
    } catch {
      return null;
    }
  });
  const [data, setData] = useState({ projects: [], tasks: [], team: [], customers: [], documents: [], dashboard: null });
  const [loading, setLoading] = useState(true);
  const [notifications, setNotifications] = useState({ items: [], unread: 0 });
  const [taskId, setTaskId] = useState(null);
  const [dialog, setDialog] = useState(null); // {type, initial}

  const refresh = useCallback(async () => {
    try {
      const [p, t, tm, c, d, db] = await Promise.all([
        api.get("/projects"), api.get("/tasks"), api.get("/team"), api.get("/customers"), api.get("/documents"), api.get("/dashboard"),
      ]);
      setData({ projects: p.data, tasks: t.data, team: tm.data, customers: c.data, documents: d.data, dashboard: db.data });
    } catch (e) {
      toast.error("Could not load data from the server");
    } finally {
      setLoading(false);
    }
  }, []);

  const loadNotifications = useCallback(async () => {
    if (!user) return;
    try {
      const r = await api.get(`/notifications/${user.id}`);
      setNotifications(r.data);
      return r.data;
    } catch {
      return null;
    }
  }, [user]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  useEffect(() => {
    if (!user) return;
    loadNotifications();
    const iv = setInterval(loadNotifications, 20000);
    return () => clearInterval(iv);
  }, [user, loadNotifications]);

  const login = async (member) => {
    localStorage.setItem(USER_KEY, JSON.stringify(member));
    setUser(member);
    try {
      const r = await api.get(`/notifications/${member.id}`);
      setNotifications(r.data);
      toast(`Welcome back, ${member.name.split(" ")[0]}`, {
        description: r.data.unread ? `You have ${r.data.unread} unread notification${r.data.unread > 1 ? "s" : ""}.` : "You're all caught up.",
      });
    } catch {
      /* ignore */
    }
  };
  const logout = () => {
    localStorage.removeItem(USER_KEY);
    setUser(null);
    setNotifications({ items: [], unread: 0 });
  };

  const updateTask = async (id, patch, silent) => {
    setData((d) => ({ ...d, tasks: d.tasks.map((t) => (t.id === id ? { ...t, ...patch } : t)) }));
    try {
      const r = await api.put(`/tasks/${id}`, { ...patch, actorId: user?.id });
      if (!silent) toast.success("Task updated");
      refresh();
      return r.data;
    } catch (e) {
      toast.error("Failed to update task");
      refresh();
    }
  };
  const updateProject = async (id, patch, silent) => {
    setData((d) => ({ ...d, projects: d.projects.map((p) => (p.id === id ? { ...p, ...patch } : p)) }));
    try {
      const r = await api.put(`/projects/${id}`, { ...patch, actorId: user?.id });
      if (!silent) toast.success("Project updated");
      refresh();
      return r.data;
    } catch {
      toast.error("Failed to update project");
      refresh();
    }
  };

  const value = useMemo(
    () => ({
      user, login, logout, ...data, loading, refresh, notifications, loadNotifications, setNotifications,
      taskId, openTask: setTaskId, closeTask: () => setTaskId(null),
      dialog, openDialog: (type, initial = {}) => setDialog({ type, initial }), closeDialog: () => setDialog(null),
      updateTask, updateProject,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [user, data, loading, notifications, taskId, dialog]
  );
  return <AppCtx.Provider value={value}>{children}</AppCtx.Provider>;
}

export const useApp = () => useContext(AppCtx);
